/**
 * The vocabulary of a cross-sell rule, in the admin's words.
 *
 * Keys are exactly what the API stores (models/CrossSellRule.js in
 * web-backend): contexts, offer kinds, schedule days, order place types.
 * Labels describe the moment in the guest app, not the internal name.
 */

/** Where in the guest app an offer can appear, grouped by journey. */
export const CONTEXT_GROUPS = [
  {
    label: "Room stay",
    contexts: [
      { value: "room_detail", label: "Viewing a room" },
      { value: "room_checkout", label: "Room checkout" },
      { value: "room_confirmed", label: "Room booked" },
      { value: "in_stay", label: "During the stay" },
      { value: "checkout_day", label: "Check-out day" },
    ],
  },
  {
    label: "Food",
    contexts: [
      { value: "dish", label: "Viewing a dish" },
      { value: "cart", label: "Cart" },
      { value: "food_checkout", label: "Food checkout" },
      { value: "order_delivered", label: "Order delivered" },
    ],
  },
  {
    label: "Rides & tables",
    contexts: [
      { value: "ride_confirmed", label: "Ride booked" },
      { value: "table_confirmed", label: "Table booked" },
    ],
  },
];

export const CONTEXT_LABEL = CONTEXT_GROUPS.reduce((acc, g) => {
  g.contexts.forEach((c) => { acc[c.value] = c.label; });
  return acc;
}, {});

/** What a rule offers. `list` names the catalog the item picker reads. */
export const KINDS = [
  { value: "food", label: "Dishes", one: "dish", icon: "restaurant" },
  { value: "addon", label: "Add-ons", one: "add-on", icon: "add_circle" },
  { value: "transport_service", label: "Rides", one: "ride", icon: "local_taxi" },
  { value: "table", label: "Tables", one: "table", icon: "table_restaurant" },
  { value: "room_type", label: "Room types", one: "room type", icon: "bed" },
];

export const KIND_LABEL = KINDS.reduce((acc, k) => ({ ...acc, [k.value]: k }), {});

export const DAYS = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

/** OrderFood.orderPlacetype — same labels as the order board. */
export const PLACE_TYPES = [
  { value: 1, label: "Room service" },
  { value: 2, label: "Dine-in" },
  { value: 3, label: "Pickup" },
  { value: 4, label: "Delivery" },
];

/** Display name for any catalog row the pickers list. */
export const itemName = (kind, row) => {
  if (!row) return "";
  switch (kind) {
    case "food": return row.foodName || "Untitled dish";
    case "addon": return row.foodAddOnName || "Untitled add-on";
    case "transport_service": return row.serviceName || "Untitled ride";
    case "table": {
      const seats = row.seats || row.person;
      return `Table ${row.tableNo != null ? row.tableNo : "?"}${seats ? ` · ${seats} seats` : ""}`;
    }
    case "room_type": return row.type || "Untitled room type";
    default: return row.name || String(row._id);
  }
};

/** "Mon–Fri · 11:00–15:00", "Every day", "Weekends · from 18:00"... */
export const scheduleSummary = (schedule) => {
  const s = schedule || {};
  const days = s.days || [];
  let dayText = "Every day";
  if (days.length && days.length < 7) {
    const set = new Set(days);
    const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
    if (days.length === 5 && weekdays.every((d) => set.has(d))) dayText = "Weekdays";
    else if (days.length === 2 && set.has("saturday") && set.has("sunday")) dayText = "Weekends";
    else dayText = DAYS.filter((d) => set.has(d.value)).map((d) => d.label).join(", ");
  }
  return s.fromTime && s.toTime ? `${dayText} · ${s.fromTime}–${s.toTime}` : dayText;
};
